import type { RowId } from 'v5/core/form-schema/formRows';

/**
 * The address the app writes to when it needs one email of a resource: the
 * row with the lowest id, as the backend’s `findPrimaryEmail` decides
 * (contacts have no primary flag). `null` when there is none or it is blank.
 */
export function getPrimaryEmail(
  emails: ReadonlyArray<{ id: number; value_1?: string | null }>
): string | null {
  const first = emails.reduce<(typeof emails)[number] | null>(
    (lowest, row) => (lowest === null || row.id < lowest.id ? row : lowest),
    null
  );
  return first?.value_1?.trim() || null;
}

/**
 * Which row of an edited list will be the primary email once saved: the
 * lowest saved id among filled rows, else the first new filled row (new rows
 * get higher ids). `-1` when no row is filled.
 */
export function primaryEmailIndex(
  rows: ReadonlyArray<{ rowId: RowId; value: string }>
): number {
  const filled = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => row.value.trim() !== '');
  const saved = filled.filter(({ row }) => row.rowId.type === 'persisted');
  const lowest = saved.reduce<(typeof saved)[number] | null>(
    (best, entry) =>
      best === null ||
      (entry.row.rowId.type === 'persisted' &&
        best.row.rowId.type === 'persisted' &&
        entry.row.rowId.id < best.row.rowId.id)
        ? entry
        : best,
    null
  );
  return lowest?.index ?? filled[0]?.index ?? -1;
}
